import { Card, Box, Icon, Text, SimpleGrid } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from 'react';
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import './TenantMeters.css';
import click from './sounds/shortclick.mp3';
import { FaCircle, FaCheckCircle, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function TenantMeters() {
    const [tenant1Elect, setTenant1Elect] = useState([]);
    const [tenant2Elect, setTenant2Elect] = useState([]);
    const [tenant1Water, setTenant1Water] = useState([]);
    const [tenant2Water, setTenant2Water] = useState([]);
    const [displayDev, setDisplayDev] = useState([]);
    const [retry, setRetry] = useState(false);
    const [hoveredDeviceId, setHoveredDeviceId] = useState(null);
    const [chartData, setChartData] = useState({});
    const isInitialLoad = useRef(true);

    const playClickSound = () => {
        const audio = new Audio(click);
        audio.play();
        audio.onended = () => { }
    };

    // Create a connection to the hub
    const connection = new signalR.HubConnectionBuilder()
        .withUrl("/devicehub")
        .build();

    const handleCellEnter = (deviceId) => {
        setHoveredDeviceId(deviceId);
        playClickSound();
    };

    const handleCellLeave = () => {
        setHoveredDeviceId(null);
    };

    // Function to fetch chart data for a device (7 days)
    /*const fetchChartData = async (deviceId, unit) => {
        try {
            const response = await axios.get(`https://futipiot.ddns.net/api/meters/tenant-history/multiple?deviceIds=${deviceId}&timeframe=24h`);

            if (response.data && response.data.length > 0) {
                const deviceData = response.data[0];

                // Process the data for the chart - group by day and calculate daily averages
                const dailyData = {};
                deviceData.data.forEach(item => {
                    const date = new Date(item.createdAt);
                    const dayKey = date.toLocaleDateString();

                    if (!dailyData[dayKey]) {
                        dailyData[dayKey] = {
                            date: dayKey,
                            values: [],
                            time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        };
                    }
                    dailyData[dayKey].values.push(parseFloat(item.status) || 0);
                });

                // Calculate daily averages
                const processedData = Object.values(dailyData).map(day => ({
                    date: day.date,
                    time: day.time,
                    value: day.values.reduce((sum, val) => sum + val, 0) / day.values.length,
                    fullDate: new Date(day.date)
                })).sort((a, b) => a.fullDate - b.fullDate);

                setChartData(prev => ({
                    ...prev,
                    [deviceId]: {
                        data: processedData,
                        unit: unit
                    }
                }));
            }
        } catch (error) {
            console.error(`Error fetching chart data for device ${deviceId}:`, error);
        }
    };*/
    const fetchChartData = async (deviceId, unit) => {
        try {
            const response = await axios.get(
                `https://futipiot.ddns.net/api/meters/tenant-history/multiple?deviceIds=${deviceId}&timeframe=24h`
            );

            if (response.data && response.data.length > 0) {
                const deviceData = response.data[0];

                // Plot every raw data point — no grouping, no averaging
                const processedData = deviceData.data
                    .map(item => {
                        const date = new Date(item.createdAt);
                        return {
                            date: date.toLocaleDateString(),                       // e.g. 5/14/2025
                            time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), // e.g. 14:35
                            fullDate: date,
                            value: parseFloat(item.status) || 0
                        };
                    })
                    .sort((a, b) => a.fullDate - b.fullDate);

                setChartData(prev => ({
                    ...prev,
                    [deviceId]: {
                        data: processedData,
                        unit: unit
                    }
                }));
            }
        } catch (error) {
            console.error(`Error fetching chart data for device ${deviceId}:`, error);
        }
    };

    // Function to fetch chart data for all devices
    const fetchAllChartsData = (devices) => {
        devices.forEach(device => {
            if (device.id) {
                fetchChartData(device.id, device.unit);
            }
        });
    };

    const DeviceCell = ({ device, isHovered, scaleFactor = 1, labelOverride = null }) => {
        const cellStyle = {
            minWidth: "350px", // Increased width to accommodate chart with axes
            height: "350px",
            fontSize: "16px",
            overflow: "hidden",
            color: "#f7f4ef",
            backgroundColor: isHovered ? "#1a237e" : "#525252",
            transition: "all 0.3s ease",
            borderRadius: "8px",
            padding: "12px",
            margin: "8px",
            boxShadow: isHovered ? "0 0 10px rgba(255,215,0,0.5)" : "none",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between"
        };

        const currentChartData = chartData[device.id];

        // Compute scaled chart data if needed
        const scaledChartData = currentChartData
            ? currentChartData.data.map(point => ({
                ...point,
                value: point.value * scaleFactor
            }))
            : [];

        // Compute custom Y-axis domain based on scaled data
        let yDomain = ['auto', 'auto'];
        if (scaledChartData.length > 0) {
            const values = scaledChartData.map(d => d.value);
            const dataMin = Math.min(...values);
            const dataMax = Math.max(...values);
            const range = dataMax - dataMin;

            if (range < 0.05) {
                const mid = (dataMin + dataMax) / 2;
                yDomain = [mid - 0.025, mid + 0.025];
            } else {
                yDomain = [dataMin, dataMax];
            }
        }

        return (
            <div
                key={device.id}
                className="metercellstyle"
                style={cellStyle}
                onMouseEnter={() => handleCellEnter(device.id)}
                onMouseLeave={handleCellLeave}
            >
                {/* Left side - Device info and status */}
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    width: "35%",
                    paddingRight: "10px"
                }}>
                    <div style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        textAlign: "center",
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "8px"
                    }}>
                        {/* Use labelOverride if provided, otherwise use device.label */}
                        {labelOverride || device.label}
                    </div>
                    <div
                        className="meterstatusstyle"
                        style={{
                            fontSize: "20px",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "#f5f0e9",
                            backgroundColor: device.type === 1 ? "#d32f2f" : "#1976d2",
                            borderRadius: "8px",
                            padding: "8px 6px",
                            boxShadow: isHovered ? "0 0 8px rgba(255,215,0,0.7)" : "none"
                        }}
                    >
                        {/* Apply scale factor to displayed status */}
                        {(device.status * scaleFactor).toFixed(3)} {device.unit}
                    </div>
                </div>

                {/* Right side - Chart */}
                <div style={{
                    width: "65%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "4px",
                    padding: "4px"
                }}>
                    {currentChartData ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={scaledChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#666" />
                                <XAxis
                                    dataKey="time"
                                    stroke="#ccc"
                                    fontSize={10}
                                    tick={{ fill: '#ccc' }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={40}
                                    interval="preserveStartEnd"   // avoids overlapping ticks when there are many points
                                />
                                <YAxis
                                    stroke="#ccc"
                                    fontSize={10}
                                    tick={{ fill: '#ccc' }}
                                    width={40}
                                    domain={yDomain}
                                    tickFormatter={(value) => value.toFixed(3)}
                                    label={{
                                        value: currentChartData.unit,
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fill: '#ccc', fontSize: 10 }
                                    }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#333',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: '#fff'
                                    }}
                                    labelStyle={{ color: '#ffc107', fontWeight: 'bold' }}
                                    formatter={(value) => [`${value.toFixed(3)} ${currentChartData.unit}`, 'Value']}
                                    labelFormatter={(label, payload) => {
                                        if (payload && payload.length > 0) {
                                            const p = payload[0].payload;
                                            return `${p.date} ${p.time}`;
                                        }
                                        return label;
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#ffc107"
                                    strokeWidth={2}
                                    dot={{ fill: '#ffc107', strokeWidth: 2, r: 3 }}
                                    activeDot={{ r: 5, fill: '#ff5722' }}
                                    isAnimationActive={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            color: "#ccc",
                            fontSize: "12px"
                        }}>
                            Loading chart...
                        </div>
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        console.log("Updating web page...");
        const httpClient = axios.create({
            baseURL: 'https://futipiot.ddns.net/',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const getData = async () => {
            try {
                const response = await httpClient.get('/api/meters');
                const deviceDetails = response.data.map((item) => ({
                    id: item.deviceId,
                    deviceName: item.deviceName,
                    deviceEui: item.deviceEui,
                    label: item.label,
                    status: item.status,
                    type: item.type,
                    data_type: item.dataType,
                    alive: item.alive,
                    data: item.data,
                    channel: item.channel,
                    active: item.active,
                    unit: item.unit
                }));

                setDisplayDev(deviceDetails);

                const tenant1ElectDevices = deviceDetails.filter(d => d.data_type === 8 && d.active === 1);
                setTenant1Elect(tenant1ElectDevices);

                const tenant1WaterDevices = deviceDetails.filter(d => d.data_type === 27 && d.active === 1);
                setTenant1Water(tenant1WaterDevices);

                const tenant2ElectDevices = deviceDetails.filter(d => d.data_type === 8 && d.active === 1);
                setTenant2Elect(tenant2ElectDevices);

                const tenant2WaterDevices = deviceDetails.filter(d => d.data_type === 27 && d.active === 1);
                setTenant2Water(tenant2WaterDevices);

                // Fetch chart data for all devices
                const relevantDevices = deviceDetails.filter(d => d.data_type === 8 || d.data_type === 27);
                fetchAllChartsData(relevantDevices);

            } catch (error) {
                console.error(error);
                setRetry(true);
            }
        };

        getData();
    }, [retry]);

    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://futipiot.ddns.net/',
            headers: { 'Content-Type': 'application/json' }
        });

        const getData = async () => {
            try {
                const response = await httpClient.get('/api/meters');
                const deviceDetails = response.data.map((item) => ({
                    id: item.deviceId,
                    deviceName: item.deviceName,
                    deviceEui: item.deviceEui,
                    label: item.label,
                    status: item.status,
                    type: item.type,
                    data_type: item.dataType,
                    alive: item.alive,
                    data: item.data,
                    channel: item.channel,
                    active: item.active,
                    unit: item.unit
                }));

                setDisplayDev(deviceDetails);

                const tenant1ElectDevices = deviceDetails.filter(d => d.data_type === 8 && d.active === 1);
                setTenant1Elect(tenant1ElectDevices);

                const tenant1WaterDevices = deviceDetails.filter(d => d.data_type === 27 && d.active === 1);
                setTenant1Water(tenant1WaterDevices);

                const tenant2ElectDevices = deviceDetails.filter(d => d.data_type === 8 && d.active === 1);
                setTenant2Elect(tenant2ElectDevices);

                const tenant2WaterDevices = deviceDetails.filter(d => d.data_type === 27 && d.active === 1);
                setTenant2Water(tenant2WaterDevices);

                // Fetch chart data for all devices
                const relevantDevices = deviceDetails.filter(d => d.data_type === 8 || d.data_type === 27);
                fetchAllChartsData(relevantDevices);

                if (isInitialLoad.current) {
                    isInitialLoad.current = false;
                }
            } catch (error) {
                console.error(error);
            }
        };

        connection
            .start()
            .then(() => console.log("Connected to the hub"))
            .catch((err) => console.error(err));

        connection.on("ReceiveDeviceData", () => {
            console.log("Updating....");
            getData();
        });

        return () => {
            connection.off("ReceiveDeviceData");
        };
    }, []);

    const DeviceList = () => {
        return (
            <div style={{ width: '100%' }}>
                <table>
                    {/* Tenant 1 Section */}
                    <div style={{
                        backgroundColor: '#888888',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '24px'
                    }}>
                        <td>
                            <h3 style={{
                                width: '100%',
                                padding: '12px',
                                margin: '0 0 16px 0',
                                color: '#ffffff',
                                fontSize: '28px',
                                fontWeight: '700',
                                backgroundColor: '#ff7f27',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>住戶#1狀況</h3>
                        </td>
                        <td>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '20px'
                            }}>
                                {/* Electricity Column - using Tenant 2 data divided by 8, label override */}
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center'
                                    }}>
                                        {Array.isArray(tenant1Elect) && tenant1Elect.map(device => (
                                            <DeviceCell
                                                key={device.id}
                                                device={device}
                                                isHovered={hoveredDeviceId === device.id}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Water Column */}
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center'
                                    }}>
                                        {Array.isArray(tenant1Water) && tenant1Water.map(device => (
                                            <DeviceCell
                                                key={device.id}
                                                device={device}
                                                isHovered={hoveredDeviceId === device.id}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </td>
                    </div>
                </table>
                <table>
                    {/* Tenant 2 Section */}
                    <div style={{
                        backgroundColor: '#888888',
                        borderRadius: '12px',
                        padding: '16px'
                    }}>
                        <td>
                            <h3 style={{
                                width: '100%',
                                padding: '12px',
                                margin: '0 0 16px 0',
                                color: '#ffffff',
                                fontSize: '28px',
                                fontWeight: '700',
                                backgroundColor: '#f411f4',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>住戶#2狀況</h3>
                        </td>
                        <td>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '20px'
                            }}>
                                {/* Electricity Column */}
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center'
                                    }}>
                                        {Array.isArray(tenant2Elect) && tenant2Elect.map(device => (
                                            <DeviceCell
                                                key={device.id}
                                                device={device}
                                                isHovered={hoveredDeviceId === device.id}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Water Column */}
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center'
                                    }}>
                                        {Array.isArray(tenant2Water) && tenant2Water.map(device => (
                                            <DeviceCell
                                                key={device.id}
                                                device={device}
                                                isHovered={hoveredDeviceId === device.id}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </td>
                    </div>
                </table>
            </div>
        );
    };

    return (
        <div className="containerstyle" style={{ padding: '20px' }}>
            <DeviceList />
        </div>
    );
}